import moment, { Moment } from 'moment-timezone';
import { TimeIn12HourClock } from 'ui/datePicker';
import { SkipDaysOfWeek, StartNodeData } from 'modules/workflows/types';
import { RECURRENCE_SCHEDULE_TYPES_WITH_SKIP_ON_DAYS, TIME_FORMAT } from './Scheduler.constants';
import {
  Day,
  EndRecurrenceType,
  RecurrenceScheduleType,
  SchedulerFormStateValues,
  SRLNodeData,
  SRLStateValues,
} from './Scheduler.types';

export const DEFAULT_SKIP_DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
].reduce((acc, day) => ({ ...acc, [day]: false }), {});

/**
 *
 * Most of the date and time conversions in this file are adapated or
 * copied over from the Angular implementation of Scheduled Recurring
 * List Trigger. The primary goal of these utils is to _replicate_
 * the functionality and usage of the existing version for
 * cross-compatibility, so if some of the manipulations going on below
 * seem bizarre, that's why ¯\_(ツ)_/¯
 *
 * Should we probably revisit all of this at some point? Most likely.
 *
 * A weird quirk this implemention includes is mixing values from the
 * local time zone and project's set time zone, which may differ
 */
export const updateToClosestFutureHalfHour = (mt: Moment) => {
  const remainder = 30 - (mt.minute() % 30);
  return mt.add(remainder, 'm');
};

// used to format time to send to backend
export const getMinutesFromMidnight = (time: Moment) => time.hours() * 60 + time.minutes();

// From NodeUtilsService.js
export const getSetStartTime = (minuteOfDay: number) => {
  const hour = Math.floor(minuteOfDay / 60) % 12;
  const minute = minuteOfDay % 60;
  const minuteStr = minute < 10 ? `0${minute}` : minute;
  const ampm = minuteOfDay < 720 ? 'AM' : 'PM';
  return moment(`${hour}:${minuteStr} ${ampm}`, 'HH:mm A');
};

// From momentTz() in editWorkflowController.js
const momentTz = (startAt: number | string | Moment, timeZone: string) =>
  moment.tz(startAt, timeZone);

// From epochToLocalMoment() in editWorkflowController.js
export const epochToLocalMoment = (epochMs: number | string | Moment, timeZone: string) => {
  const rawDate = momentTz(epochMs, timeZone);
  // Convert start date & time to local time set by browser
  return moment()
    .year(rawDate.year())
    .month(rawDate.month())
    .date(rawDate.date())
    .hour(rawDate.hour())
    .minute(rawDate.minute())
    .second(0)
    .millisecond(0);
};

// From updateStartDate() in editWorkflow.controller.js
export const parseStartDateForSaving = (startDate: Moment, timeZone: string) =>
  momentTz(startDate, timeZone)
    .year(startDate.year())
    .month(startDate.month())
    .date(startDate.date())
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
    .valueOf();

// From updateEndDate() in editWorkflow.controller.js
export const parseEndDateForSaving = (endDate: Moment, timeZone: string) =>
  endDate
    ? momentTz(endDate, timeZone)
        .year(endDate.year())
        .month(endDate.month())
        .date(endDate.date())
        .hour(23)
        .minute(59)
        .second(0)
        .millisecond(0)
        .valueOf()
    : null;

// returns default scheduled recurring list trigger data based on given timezone
export const getSRLDefaultStateValues = (timeZone = '') => {
  const currentMoment = moment();
  const startDate = epochToLocalMoment(currentMoment.valueOf(), timeZone);

  return {
    momentEnd: null,
    minStart: startDate,
    recurDays: '1',
    skipDaysOfWeek: DEFAULT_SKIP_DAYS_OF_WEEK,
    momentStart: startDate,
    momentStartTime: updateToClosestFutureHalfHour(currentMoment),
    timeZone,
  };
};

// returns scheduled recurring list state data based on given saved node data
export const getSRLStateValues = (nodeData: SRLNodeData) => {
  const { minuteOfDay, recurDays, skipDaysOfWeek, startDate, timeZone, endDate } = nodeData;

  return {
    momentEnd: endDate ? epochToLocalMoment(endDate, timeZone) : null,
    minStart: epochToLocalMoment(moment().valueOf(), timeZone),
    recurDays: recurDays?.toString?.(),
    skipDaysOfWeek,
    momentStart: epochToLocalMoment(startDate, timeZone),
    momentStartTime: getSetStartTime(minuteOfDay),
    timeZone,
  };
};

// transforms scheduled recurring list trigger data to format expected by backend for saving
export const getSRLNodeDataValues = (stateData: SRLStateValues) => {
  const { momentEnd, recurDays, skipDaysOfWeek, momentStart, momentStartTime, timeZone } =
    stateData;

  return {
    endDate: momentEnd ? parseEndDateForSaving(momentEnd, timeZone) : null,
    minuteOfDay: getMinutesFromMidnight(momentStartTime),
    recurDays: Number(recurDays),
    skipDaysOfWeek,
    startDate: parseStartDateForSaving(momentStart, timeZone),
    timeZone,
  };
};

/**
 * The utils below are all used when the `startTileRefresh` ff is on
 */

/**
 * Returns default minimum start date for UI
 */
export const getMinStartDate = (timeZone = '') => epochToLocalMoment(moment().valueOf(), timeZone);

/**
 * returns a default start time when one isn't set by the user for UI
 */
export const getDefaultStartTime = () => {
  const currentMoment = moment();
  const defaultStartDateTime = updateToClosestFutureHalfHour(currentMoment.clone());
  return defaultStartDateTime.format(TIME_FORMAT) as TimeIn12HourClock;
};

/**
 * used to format time to send to backend based off selected start time for saving
 */
export const parseStartTimeForSaving = (startTime: TimeIn12HourClock) =>
  getMinutesFromMidnight(moment(startTime, 'HH:mm A'));

/**
 * maps days (number) to a recurring schedule type for UI
 */
const daysToRecurrenceMap = new Map([
  [1, RecurrenceScheduleType.Daily],
  [7, RecurrenceScheduleType.Weekly],
  [14, RecurrenceScheduleType.BiWeekly],
  [30, RecurrenceScheduleType.ThirtyDays],
]);
/**
 * maps recurring schedule type to days (number) for saving
 */
const recurrenceToDaysMap = new Map(
  Array.from(daysToRecurrenceMap, (entry) => [entry[1], entry[0]]),
);

/**
 * returns form values based on recurDays for UI
 */
const getRecurrenceValues = (
  startDate?: string | number,
  endDate?: string | number,
  recurDays?: number,
) => {
  // In case dates are the same or the form is new, default to "Don't repeat"
  if (startDate === endDate || !recurDays) {
    return {
      recurrence: RecurrenceScheduleType.DoNotRepeat,
      recurDays: 1,
    };
  }
  if (recurDays && daysToRecurrenceMap.get(recurDays)) {
    return {
      recurrence: daysToRecurrenceMap.get(recurDays),
      recurDays,
    };
  }
  return {
    recurrence: RecurrenceScheduleType.Custom,
    recurDays,
  };
};

export const DAYS_OF_THE_WEEK = Object.values(Day);

/**
 * Convert `skipDaysOfWeek`(object) to `skipDays`(array) for UI
 */
export const skipDaysMapperToList = (skipDaysOfWeek?: SkipDaysOfWeek) => {
  return Object.entries(skipDaysOfWeek || {}).reduce(
    (acc: Day[], [key, value]: [string, boolean]) => {
      if (DAYS_OF_THE_WEEK.includes(key as Day) && value) {
        acc.push(key as Day);
      }

      return acc;
    },
    [],
  );
};

/**
 * Convert `skipDays`(array) to `skipDaysOfWeek`(object) for saving
 */
export const getSkipDaysOfWeek = (skipDays: Day[], recurrence: RecurrenceScheduleType) => {
  const skipDaysSet = new Set(skipDays);
  const isRecurrenceWithSkipDays = RECURRENCE_SCHEDULE_TYPES_WITH_SKIP_ON_DAYS.includes(recurrence);

  const skipDaysOfWeek = DAYS_OF_THE_WEEK.reduce(
    (days, day) => {
      const shouldSkip = isRecurrenceWithSkipDays && skipDaysSet.has(day);

      return { ...days, [day]: shouldSkip };
    },
    {} as Record<Day, boolean>,
  );

  return skipDaysOfWeek;
};

/**
 * returns scheduled recurring list state data based on given saved node data
 */
export const getDefaultFormStateValues = (
  nodeData: Partial<StartNodeData>,
  projectTimeZone = '',
  isStartTimeOptEnabled = false,
) => {
  const {
    minuteOfDay,
    startDate,
    timeZone,
    recurDays,
    endDate,
    skipDaysOfWeek,
    maxStartTimeOffsetInMinutes,
  } = nodeData;

  return {
    minuteOfDay:
      typeof minuteOfDay === 'number' && minuteOfDay >= 0
        ? (getSetStartTime(minuteOfDay).format(TIME_FORMAT) as TimeIn12HourClock)
        : getDefaultStartTime(),
    startDate:
      startDate && timeZone
        ? epochToLocalMoment(startDate, timeZone)
        : epochToLocalMoment(moment().clone().valueOf(), projectTimeZone),
    timeZone: timeZone || projectTimeZone,
    ...getRecurrenceValues(startDate, endDate, recurDays),
    endType: EndRecurrenceType.Never,
    ...(endDate &&
      timeZone && {
        endDate: epochToLocalMoment(endDate, timeZone),
        endType: EndRecurrenceType.OnDate,
      }),
    skipDaysOfWeek: skipDaysMapperToList(skipDaysOfWeek),
    ...(isStartTimeOptEnabled && {
      maxStartTimeOffsetInMinutes: startDate ? maxStartTimeOffsetInMinutes || 0 : 30,
    }),
  };
};

/**
 * transforms scheduled recurring list trigger data to format expected by backend for saving
 */
export const getFormattedSchedulerFormValues = (
  values: SchedulerFormStateValues,
  isStartTimeOptEnabled = false,
): SRLNodeData => {
  const {
    endDate,
    endType,
    timeZone,
    minuteOfDay,
    recurrence,
    recurDays,
    startDate,
    skipDaysOfWeek,
    maxStartTimeOffsetInMinutes,
  } = values;

  const parsedStartDate = parseStartDateForSaving(startDate, timeZone);
  let parsedEndDate;
  let parsedRecurDays = recurrenceToDaysMap.get(recurrence) || Number(recurDays);
  let parsedMaxStartTimeOffset;

  if (endDate && endType === EndRecurrenceType.OnDate) {
    parsedEndDate = parseEndDateForSaving(endDate, timeZone) as number;
  }
  if (recurrence === RecurrenceScheduleType.DoNotRepeat) {
    parsedEndDate = parsedStartDate;
    parsedRecurDays = 1;
  }
  if (isStartTimeOptEnabled && maxStartTimeOffsetInMinutes) {
    parsedMaxStartTimeOffset = maxStartTimeOffsetInMinutes;
  }
  return {
    endDate: parsedEndDate,
    minuteOfDay: parseStartTimeForSaving(minuteOfDay),
    recurDays: parsedRecurDays,
    skipDaysOfWeek: getSkipDaysOfWeek(skipDaysOfWeek, recurrence),
    startDate: parsedStartDate,
    timeZone,
    maxStartTimeOffsetInMinutes: parsedMaxStartTimeOffset,
  };
};
